import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO, injectJSONLD } from '@/hooks/useSEO';
import { navCategories } from '@/mocks/navigationData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'hot' | 'new'>('hot');

  const category = navCategories.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    if (!category) {
      navigate('/category/sousuo-yinqing', { replace: true });
    }
  }, [category, navigate]);

  const effectiveCategory = category || navCategories[0];

  useSEO({
    title: `${effectiveCategory.name} - 蓝导航网址导航`,
    description: effectiveCategory.description,
    keywords: `${effectiveCategory.name},网址导航,蓝导航,网站推荐,${effectiveCategory.sites.map((s) => s.name).join(',')}`,
    canonicalUrl: `https://www.lan-dao.com/category/${effectiveCategory.slug}`,
  });

  useEffect(() => {
    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${effectiveCategory.name} - 蓝导航`,
      description: effectiveCategory.description,
      url: `https://www.lan-dao.com/category/${effectiveCategory.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '蓝导航',
        url: 'https://www.lan-dao.com/',
      },
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.lan-dao.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: effectiveCategory.name,
        },
      ],
    });
  }, [effectiveCategory]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p className="text-foreground-500">跳转中...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <Breadcrumb />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-2xl`}></i>
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <div className="flex items-center rounded-full bg-background-100 p-1">
            <button
              onClick={() => setActiveTab('hot')}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === 'hot'
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              热门推荐
            </button>
            <button
              onClick={() => setActiveTab('new')}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === 'new'
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              最新收录
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-product-shop="">
          {category.sites.map((site) => (
            <a
              key={site.id}
              href={site.url}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="group flex items-start gap-3.5 p-4 bg-background-50 rounded-xl border border-background-200 hover:border-accent-300 transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors duration-200">
                <i className={`${site.icon} text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {site.name}
                  </h3>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  {site.description}
                </p>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {site.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-2 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[10px] whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 pt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <i className="ri-external-link-line text-foreground-400 text-sm"></i>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 border-t border-background-200 pt-8">
          <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">浏览其他分类</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {navCategories
              .filter((c) => c.slug !== category.slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-accent-100 hover:text-accent-700 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}